import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../css/workflow.css";
import MainBreadcrumb from "../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/page-main-bcb/ts/main-bcb";
import SelectField from "../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField";
import SearchField from "../../../../component-controller-mod/component-serv/input-fs-cp/search-field-op/ts/searchField";
import {
  columns,
  getContentVersionIndexApi,
  normalizeSearch,
  status,
  toBadgeVariant,
  TypeBussiness,
} from "./workflow.type";
import { Table, Pagination, Empty } from "@core/uilibrary";
import type { ContentVersionItem, WorkflowRow } from "./workflow.type";
import { DashboardIconSmall, EyeIcon } from "../../../../asset-controller-mod/asset-serv/icons/iconExporter";
import { useRightSidebar } from "../../../../context-controller-mod/context-serv/global-provider/right-sidebar-provider/ts/right-sidebar-context";
import { useNavigate } from "react-router-dom";
import UsrbWorkflow from "./usrb-workflow";
import CancelButton from '../../../../component-controller-mod/component-serv/button-fs-cp/cancel-button-op/ts/cancelButton';
import WorkflowStatusBadge from "../../../../component-controller-mod/component-serv/badge-fs-cp/dynamic-badge-op/ts/statusBadgeWorkflow";

function getPageKey(item: ContentVersionItem) {
  const keys = item.data ? Object.keys(item.data) : [];
  return keys[0] ?? "-";
}

function getTitleFromContent(item: ContentVersionItem): string {
  const pageKey = getPageKey(item);
  const node = item.data?.[pageKey] as any;

  return (
    node?.title ||
    node?.headline ||
    node?.cta?.[0]?.heroSection?.headline ||
    node?.cta?.[0]?.heroSection?.title ||
    node?.cta?.[0]?.submitJobSection?.content?.[0]?.headerSection?.title ||
    node?.cta?.[0]?.saleContactSection?.content?.[0]?.headerSection?.title ||
    node?.cta?.[0]?.bussinessContactSection?.content?.[0]?.headerSection?.title ||
    node?.cta?.[0]?.contactSection?.content?.[0]?.headerSection?.title ||
    item.summary ||
    "-"
  );
}

function getAuthorFromContent(item: ContentVersionItem): string {
  const pageKey = getPageKey(item);
  const node = item.data?.[pageKey] as any;

  if (typeof node?.author === "string" && node.author.trim()) return node.author;

  const deepAuthor =
    node?.cta?.[0]?.layout?.content?.[0]?.footerLeft?.author ||
    node?.cta?.[0]?.layout?.content?.[0]?.footerRight?.author ||
    node?.cta?.[0]?.layout?.content?.[0]?.headerSection?.author ||
    node?.cta?.[0]?.heroSection?.author ||
    node?.cta?.[0]?.positionSection?.author ||
    node?.cta?.[0]?.submissionStepSection?.author ||
    node?.cta?.[0]?.contactSection?.author ||
    node?.cta?.[0]?.saleContactSection?.content?.[0]?.headerSection?.author ||
    node?.cta?.[0]?.bussinessContactSection?.content?.[0]?.headerSection?.author ||
    node?.cta?.[0]?.submitJobSection?.content?.[0]?.headerSection?.author;

  if (typeof deepAuthor === "string" && deepAuthor.trim()) return deepAuthor;

  return "-";
}

function getPreviewStatusRoute(
  status?: string
): "draft" | "published" | "unpublished" {
  const value = String(status ?? "").toLowerCase();

  if (value === "published") return "published";

  if (
    value === "unpublished" ||
    value === "unpublish" ||
    value === "rejected"
  ) {
    return "unpublished";
  }

  return "draft";
}

const WebflowUi = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<WorkflowRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const ursb = useRightSidebar();
  const [allVersions, setAllVersions] = useState<ContentVersionItem[]>([]);
  const navigate = useNavigate()

  const [statusValue, setStatusValue] = useState("");
  const [typeValue, setTypeValue] = useState("");

  const [reloadKey, setReloadKey] = useState(0);

  const [searchValue, setSearchValue] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const t = globalThis.window.setTimeout(
      () => setSearchDebounced(normalizeSearch(searchValue)),
      400
    );
    return () => globalThis.window.clearTimeout(t);
  }, [searchValue]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setLoading(true);

        const res = await getContentVersionIndexApi();
        if (!alive) return;

        const all = res?.data?.data ?? [];

        // sort latest first
        const sorted = [...all].sort((a, b) => {
          const aTime = new Date(a.createdAt ?? 0).getTime();
          const bTime = new Date(b.createdAt ?? 0).getTime();
          return bTime - aTime;
        });

        setAllVersions(sorted);

        const filtered = sorted.filter((it) => {

          const statusFilter = statusValue || "all";
          const typeFilter = typeValue || "all";

          if (
            statusFilter !== "all" &&
            String(it.status ?? "").toLowerCase() !== statusFilter.toLowerCase()
          ) {
            return false;
          }

          if (
            typeFilter !== "all" &&
            getPageKey(it) !== typeFilter
          ) {
            return false;
          }

          if (searchDebounced) {
            const hay =
              `${getTitleFromContent(it)} ${getPageKey(it)} ${getAuthorFromContent(it)} ${it.status ?? ""}`.toLowerCase();

            if (!hay.includes(searchDebounced.toLowerCase())) return false;
          }

          return true;
        });

        setTotalItems(filtered.length);

        const start = (currentPage - 1) * rowsPerPage;
        const pageItems = filtered.slice(start, start + rowsPerPage);

        const mapped: WorkflowRow[] = pageItems.map((item, idx) => {
          const sequentialId = start + idx + 1;

          return {
            id: item.contentVersionUuid,
            ID: String(sequentialId),
            Page: getPageKey(item),
            Title: getTitleFromContent(item),
            Version: String(item.version ?? "-"),
            "Create date": item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-",
            Author: getAuthorFromContent(item),
            Status: item.status ?? "-",
            _raw: item,
          };
        });

        setRows(mapped);
      } catch (e) {
        console.error("GET content version index failed:", e);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [currentPage, rowsPerPage, statusValue, typeValue, searchDebounced, reloadKey]);

  const openMenu = useCallback(() => {

    ursb.open({
      title: "Summary",
      widthSize: "400px",
      content: <UsrbWorkflow />,
    });
  }, [ursb]);

  const formattedData = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        Status: <WorkflowStatusBadge variant={toBadgeVariant(row.Status)} label={row.Status} />,
        Action: (
          <div className="scl--workflow-action-const-sty">
            <button 
              className="scl--workflow-eye-btn-const-sty"
              type="button"
              onClick={() => {
                const previewStatus = getPreviewStatusRoute(row.Status);

                navigate(
                  `/workflow/manage/preview/${row?._raw?.contentUuid}/${row?._raw?.contentVersionUuid}/${previewStatus}`,
                  {
                    state: {
                      allVersions,
                    },
                  }
                );
              }}
            >
              <EyeIcon />
            </button>
          </div>
        ),
      })),
      
    [rows, allVersions, navigate]
  );

  const hasActiveFilters = Boolean(
    statusValue || typeValue || searchValue
  );

  const handleClearFilters = useCallback(() => {
    setStatusValue("");
    setTypeValue("");
    setSearchValue("");
    setCurrentPage(1);
  }, []);


  return (
    <main className="scl--workflow-main-content">
      <div className="scl--workflow-head-const-box">
        <MainBreadcrumb
          title="Workflow"
          paragraph="Manage content approval workflow and track submission status."
        />
      </div>

      <div className="scl--workflow-filt-const-box">
        <div className="scl--workflow-filt-status">
          <SelectField
            name="status"
            placeholder="Status"
            label="Status"
            options={status}
            value={statusValue}
            onChange={(v) => {
              setStatusValue(String(v));
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="scl--workflow-filt-type">
          <SelectField
            name="type"
            placeholder="Type"
            label="Type"
            options={TypeBussiness}
            value={typeValue}
            onChange={(v) => {
              setTypeValue(String(v));
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="scl--workflow-filt-search">
          <SearchField
            name="search"
            placeholder="Search"
            label="Search"
            value={searchValue}
            onChange={(v) => {
              setSearchValue(v);
              setCurrentPage(1);
            }}
          />
        </div>
        {hasActiveFilters && (
          <CancelButton onClick={handleClearFilters} aria-label="Clear all filters" />
        )}

        <div className="scl-workflow-filt-summ">
          <button className="btn-sum" onClick={() => openMenu()}>
            <DashboardIconSmall />
          </button>
        </div>
      </div>

      <div className="scl--workflow-tabl-const-box">
        {rows.length === 0 && !loading ? (
          <Empty
            title="No Data yet"
            description="There's nothing here yet. Start by adding new data to see it listed here."
          />
        ) : (
          <>
            <Table
              key={rows.map((r) => `${r.id}:${r.Status}`).join("|")}
              columns={columns}
              data={formattedData}
              showCheckbox={false}
              showId={false}
              showPagination={false}
              rowsPerPageOptions={[5, 10, 20]}
              defaultRowsPerPage={10}
              backgroundColor="transparent"
              clickableColumns={["Title"]}
              onCellClick={(row: any) => {
                const previewStatus = getPreviewStatusRoute(row?._raw?.status);

                navigate(
                  `/workflow/manage/preview/${row?._raw?.contentUuid}/${row?._raw?.contentVersionUuid}/${previewStatus}`,
                  {
                    state: {
                      allVersions,
                    },
                  }
                );
              }}
              onSelectionChange={(selectedRowIds: string[]) => {
                setSelectedIds(new Set(selectedRowIds));
              }}
              selectColumnWidth={49}
              columnWidths={{
                ID: "4%",
                Page: "10%",
                Title: "12%",
                Version: "14%",
                "Create date": "15%",
                Author: "15%",
                Status: "15%",
                Action: "13%",
              }}
            />

            <Pagination
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              rowsPerPageOptions={[5, 10, 20]}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalItems={totalItems}
            />
          </>
        )}
      </div>
    </main>
  );
};

export default WebflowUi;